import Component from '@glimmer/component'
import { service } from '@ember/service'
import type Store from '@ember-data/store'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { t } from 'ember-intl'
import { concat } from '@ember/helper'
import { trackedFunction } from 'reactiveweb/function'
import { groupBy } from 'lodash'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { BigBookCard } from 'teacher-ui-client/components/big-books/card'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import FidgetSpinnerWave from '@blakeelearning/fidget/components/fidget/spinner/wave'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'

interface Signature {
  Element: HTMLDivElement
  Args: {
    subscriptionType: SubscriptionType
    keywords: string
    bigBookLevel: string
    bigBookLevelRange: string[]
  }
}

export class BigBooksSearch extends Component<Signature> {
  @service store!: Store

  loadBooks = trackedFunction(this, async () => {
    const bookQuery: Record<string, string | boolean | number> = {
      bigBooks: true,
      subscriptionType: this.args.subscriptionType,
      excluded: false,
    }
    if (this.args.bigBookLevel !== 'all') bookQuery.bigBookLevel = this.args.bigBookLevel
    if (this.args.keywords) bookQuery.keywords = this.args.keywords

    await Promise.resolve() // disconnect tracking before store calls

    return await this.store.query<BookLibraryBook>('book-library/book', {
      filter: bookQuery,
      groupLimit: 500,
      groupOffset: 0,
    })
  })

  get booksGroupedByLevel() {
    const bookResults = this.loadBooks.value ?? []
    const grouped = groupBy(bookResults, (book) => book.bigBookLevels?.[this.args.subscriptionType])
    const sorted = Object.entries(grouped).sort(
      ([keyA], [keyB]) => this.args.bigBookLevelRange.indexOf(keyA) - this.args.bigBookLevelRange.indexOf(keyB),
    )
    return Object.fromEntries(sorted)
  }

  <template>
    <div class="relative space-y-6">
      {{#each-in this.booksGroupedByLevel as |level books|}}
        <ContentTile data-test-result-panel class="group/search">
          <h3
            data-test-result-panel-header
            {{! Colour each panel differently, looping over 4 background colours. Anchored to "group/search" }}
            {{! https://tailwindcss.com/docs/hover-focus-and-other-states#differentiating-nested-groups }}
            {{! https://tailwindcss.com/docs/hover-focus-and-other-states#arbitrary-groups }}
            class="text-dusty-black-500 group-[:nth-of-type(4n+1)]/search:bg-forest-green-50 group-[:nth-of-type(4n+2)]/search:bg-yolky-yellow-100 group-[:nth-of-type(4n+3)]/search:bg-rexy-purple-100 group-[:nth-of-type(4n+4)]/search:bg-deep-orange-50 flex w-full items-center py-3 pl-8 italic"
          >
            <span class="mr-1">
              {{t "bigBooks.bigBooksLevel"}}
              {{t (concat "bigBooks.levels." @subscriptionType) level=level}}
            </span>
            <span class="ml-2 text-xl font-normal uppercase">
              {{t "bookLibrary.components.resultPanel.groupLength" count=books.length}}
            </span>
          </h3>
          <div class="mb-8 flex flex-wrap gap-x-7 gap-y-7 p-6" data-test-result-panel-body>
            {{#each books as |book|}}
              <BigBookCard
                data-test-big-book-card
                @book={{book}}
                @fadeIn={{true}}
                @subscriptionType={{@subscriptionType}}
              />
            {{/each}}
          </div>
        </ContentTile>
      {{else}}
        {{#if this.loadBooks.isPending}}
          <FidgetSpinnerWave data-test-search-group-loader @centered={{true}} />
        {{else}}
          <NoResultsMessage
            @title={{t "bookLibrary.pages.search.empty.title"}}
            @body={{t "bookLibrary.pages.search.empty.body"}}
          />
        {{/if}}
      {{/each-in}}
    </div>
  </template>
}

export default BigBooksSearch
